import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPage } from "../lib/api";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Check, ArrowRight } from "lucide-react";

export default function Areas() {
  const [page, setPage] = useState(null);
  useEffect(() => { fetchPage("areas-covered").then(setPage).catch(() => {}); }, []);

  const sections = page?.sections || [];
  const intro = sections.find(s => s.type === "richtext" && /cover/i.test(s.title || ""));
  const postcodes = sections.find(s => s.type === "features");
  const localAreas = sections.find(s => s.type === "richtext" && /local/i.test(s.title || ""));
  const cta = sections.find(s => s.type === "cta");

  return (
    <>
      <Seo title={page?.meta_title} description={page?.meta_description} robots={page?.meta_robots} jsonLd={page?.schema_json} />
      <PageHero
        eyebrow="Service area"
        title={page?.title || "Areas we cover"}
        subtitle="Door-to-door pickup across Southampton and surrounding villages."
      />

      <div data-testid="areas-page">
        {/* Two-column: intro (left) + postcodes (right) */}
        {(intro || postcodes) && (
          <section className="py-12 md:py-16" data-testid="areas-intro-postcodes">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 lg:gap-14 items-start">
              {/* Left: We cover Southampton... */}
              {intro && (
                <div data-testid="areas-intro">
                  {intro.title && (
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-6"
                        style={{fontFamily:"Outfit, sans-serif"}}>{intro.title}</h2>
                  )}
                  <div className="prose prose-neutral max-w-none"
                       dangerouslySetInnerHTML={{ __html: intro.body || "" }} />
                </div>
              )}
              {/* Right: postcodes grid */}
              {postcodes && (
                <div data-testid="areas-postcodes">
                  {postcodes.title && (
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-6"
                        style={{fontFamily:"Outfit, sans-serif"}}>{postcodes.title}</h2>
                  )}
                  <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {(postcodes.items || []).map((p, i) => (
                      <li key={i}
                          className="flex items-center gap-2 bg-white border border-black/5 rounded-xl px-3 py-2.5"
                          data-testid={`postcode-${p.title}`}>
                        <Check className="h-4 w-4 text-[#37B160] flex-none" />
                        <span className="font-medium text-sm" style={{fontFamily:"Outfit, sans-serif"}}>{p.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Local areas — full width, 3 columns */}
        {localAreas && (
          <section className="py-12 md:py-16 bg-[#F9FAFB]" data-testid="areas-local">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {localAreas.title && (
                <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-8"
                    style={{fontFamily:"Outfit, sans-serif"}}>{localAreas.title}</h2>
              )}
              <div className="prose prose-neutral max-w-none areas-three-col"
                   dangerouslySetInnerHTML={{ __html: localAreas.body || "" }} />
            </div>
          </section>
        )}

        {/* CTA — unchanged */}
        {cta && (
          <section className="py-12 md:py-16" data-testid="areas-cta">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-[#111827] text-white rounded-3xl p-8 md:p-12 grid md:grid-cols-12 gap-6 items-center">
                <div className="md:col-span-8">
                  {cta.title && (
                    <h2 className="text-2xl sm:text-3xl tracking-tight font-medium"
                        style={{fontFamily:"Outfit, sans-serif"}}>{cta.title}</h2>
                  )}
                  {cta.subtitle && <p className="mt-2 text-white/80">{cta.subtitle}</p>}
                </div>
                <div className="md:col-span-4 md:text-right">
                  <Link to={cta.cta_to || "/book"}>
                    <Button className="bg-[#37B160] hover:bg-[#2D9450] text-white h-11 px-6">
                      {cta.cta_label || "Book your lessons"} <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
